import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useIncidents } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { AlertTriangle, Plus, Clock, CheckCircle2, XCircle, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { LinkManager } from '@/components/shared/LinkManager';
import { IncidentEditDialog } from '@/components/incidents/IncidentEditDialog';
import { IncidentCreateDialog } from '@/components/incidents/IncidentCreateDialog';

export default function IncidentsPage() {
  const { organization } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  const { incidents, isLoading, createIncident, updateIncident, deleteIncident, isDeleting } = useIncidents(organization?.id);
  
  const selectedIncident = incidents?.find(i => i.id === selectedIncidentId);

  const handleSubmit = async (data: any, links?: Array<{id: string; type: any; name: string}>) => {
    if (!data.title) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!organization?.id) {
      toast.error('No organization found');
      return;
    }

    createIncident({
      ...data,
      org_id: organization.id,
    }, {
      onSuccess: async (newIncidentData: any) => {
        if (newIncidentData?.id && links && links.length > 0) {
          const { useLinkStore } = await import('@/stores/linkStore');
          const { bulkCreateLinks } = useLinkStore.getState();
          
          for (const link of links) {
            await bulkCreateLinks('incident', newIncidentData.id, link.type, [link.id]);
          }
        }
      }
    });
    setDialogOpen(false);
  };

  const filteredIncidents = (incidents || []).filter(incident => {
    if (activeTab === 'all') return true;
    return incident.status === activeTab;
  });

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-blue-500/10 text-blue-500',
      medium: 'bg-yellow-500/10 text-yellow-500',
      high: 'bg-orange-500/10 text-orange-500',
      critical: 'bg-red-500/10 text-red-500',
    };
    return <Badge className={colors[priority] || colors.medium}>{priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-500/10 text-gray-500',
      investigating: 'bg-yellow-500/10 text-yellow-500',
      reported: 'bg-blue-500/10 text-blue-500',
      resolved: 'bg-green-500/10 text-green-500',
      closed: 'bg-gray-500/10 text-gray-500',
    };
    return <Badge className={colors[status] || colors.draft}>{status}</Badge>;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'closed':
        return <XCircle className="w-5 h-5 text-gray-500" />;
      case 'investigating':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
    }
  };

  if (isLoading) {
    return <div>Loading incidents...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Incidents</h1>
          <p className="text-muted-foreground">Track and manage security incidents</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Report Incident
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="investigating">Investigating</TabsTrigger>
          <TabsTrigger value="reported">Reported</TabsTrigger>
          <TabsTrigger value="resolved">Resolved</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {filteredIncidents.length === 0 ? (
            <p className="text-muted-foreground">No incidents in this category.</p>
          ) : (
            filteredIncidents.map((incident) => (
              <Card 
                key={incident.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedIncidentId(incident.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(incident.status)}
                      <div>
                        <CardTitle className="text-lg">{incident.title}</CardTitle>
                        <CardDescription>
                          {format(new Date(incident.discovered_at), 'PPP')}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getPriorityBadge(incident.priority)}
                      {getStatusBadge(incident.status)}
                    </div>
                  </div>
                </CardHeader>
                {incident.impact && (
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{incident.impact}</p>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      <IncidentCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSubmit}
      />

      <Sheet open={!!selectedIncidentId} onOpenChange={() => setSelectedIncidentId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedIncident && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <SheetTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {selectedIncident.title}
                  </SheetTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditDialogOpen(true)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={isDeleting}
                      onClick={() => {
                        if (selectedIncidentId) {
                          deleteIncident(selectedIncidentId);
                          setSelectedIncidentId(null);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                <div className="flex items-center gap-4">
                  {getPriorityBadge(selectedIncident.priority)}
                  {getStatusBadge(selectedIncident.status)}
                </div>
                
                <div>
                  <h3 className="text-sm font-medium mb-2">Discovered</h3>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(selectedIncident.discovered_at), 'PPP')}
                  </p>
                </div>
                
                {selectedIncident.impact && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Impact</h3>
                    <p className="text-sm text-muted-foreground">{selectedIncident.impact}</p>
                  </div>
                )}
                
                {selectedIncident.actions && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Actions Taken</h3>
                    <p className="text-sm text-muted-foreground">{selectedIncident.actions}</p>
                  </div>
                )}
                
                <Separator />
                
                <LinkManager
                  entityType="incident"
                  entityId={selectedIncident.id}
                  entityName={selectedIncident.title}
                  allowedTypes={['asset', 'system', 'zone', 'risk', 'control', 'evidence', 'supplier']}
                  showHeader={true}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <IncidentEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        incident={selectedIncident}
        onSave={(id, data) => updateIncident({ id, data })}
      />
    </div>
  );
}
